/**
 * The two readouts the vote cards are drawn from: how long is left, and what it decided.
 *
 * Pure and DOM-free, which is `saying-lines.ts`' and `notice-lines.ts`' shape and for their
 * reason — the arithmetic on a card is exactly the thing to assert, and the card it is drawn on
 * cannot be laid out in jsdom at all. Nothing here touches the wire, the clock or React.
 *
 * Two rules hold this module together, and both come from the vote mechanism's own card:
 *
 * - **THE DEADLINE IS THE ROOM'S AND THE CLOCK IS THE BROWSER'S.** `serverVoteSchema.endsAtTick`
 *   is a tick, and `GameConnection` turns it into a wall-clock instant once — `DuelView.endsAtMs`
 *   and `SpokenSaying.endsAtMs`' arrangement, so the card counts down against a browser clock
 *   rather than the view being re-published twenty times a second to move a number.
 * - **AN OPEN VOTE HAS NOTHING TO READ OUT.** There is no tally on the wire until it settles
 *   (`serverVoteSchema.result` is null while it is running, and that is a secrecy rule rather
 *   than a saving — a running count is a running instruction to join the winning side), so
 *   `readVoteResult` is only ever called with a settled one and there is deliberately no
 *   "partial" shape for it to answer with.
 */

import type { VoteOption, VoteResultSnapshot, VoteSelfSnapshot } from '@chompdome/shared/types';

/**
 * How long the results card stays on screen before it takes itself away.
 *
 * **THIS REVERSES A DECISION THIS FEATURE SHIPPED WITH, and the old argument is worth keeping
 * because half of it is still what sets the number.** The results card was dismissed by the
 * player and by nothing else, on the reasoning that a settlement is a thing that happened to the
 * reader's own mass and that a card taking itself away before it had been read would be the game
 * deciding they did not need to know. The player asked for the timer: "the modal that shows what
 * happened after the star countdown should keep the x to close out but should also have a 10
 * second timer so it closes on its own if the user doesn't want to click it." So the ✕ stays and
 * the clock is added beside it, and what survives of the old argument is the *size* of the
 * number — ten seconds is long enough to read a winner, a set of tallies and one line about your
 * own mass, which is why this is not `NOTICE_LINE_MS`' 4.5 s for a sentence or
 * `DUEL_OUTCOME_BANNER_MS`' 3.5 s for a keyword.
 *
 * **THE CLOCK IS OURS, WHERE THE VOTE'S OWN COUNTDOWN IS THE ROOM'S.** `notice-lines.ts` and
 * `duel-outcome.ts` are the precedent and both say it in as many words: a value the room owns
 * arrives as a deadline on the wire, and a value nobody else has an opinion about is a
 * presentation decision made on this side. The vote's ten *answering* seconds are the room's —
 * `serverVoteSchema.endsAtTick`, turned into a browser instant once — and are what
 * `voteSecondsLeft` reads. These ten are the reader's, are on no frame, and are why nothing
 * about the wire, the simulation, `VoteResultSnapshot` or `world-hash.ts` moves for this.
 *
 * **IT APPLIES TO EVERY VOTE RESULT AND NOT ONLY A STAR'S.** A star vote is an ordinary vote
 * whose result card carries one extra line (`VoteResultSnapshot.starOutcome`); a timer on one
 * kind and not the other would be a second rule for one caller to get wrong, and there is
 * nothing about a settlement that opened without a star that deserves to sit on screen forever
 * when one that opened with a star does not.
 */
export const VOTE_RESULT_CARD_MS = 10_000;

/** One answer, as the results card draws it. */
export interface VoteResultRow {
  id: string;
  label: string;
  votes: number;
  /**
   * This answer's share of the votes that were actually counted, 0..1 — 0 for a vote nobody
   * answered.
   *
   * Of the *counted* votes rather than of the room, because the room is not a denominator this
   * side has: the tallies are over the voters who were still in the world at resolution (a voter
   * who died drops out of both sides entirely — `VoteTally`), and abstaining is a silent opt-out
   * that is never reported at all. A bar drawn against the population would be a bar that was
   * wrong about every vote most of the room ignored, which is most of them.
   */
  share: number;
  /** Whether this is the option that won — `VoteResultSnapshot.winner`, never re-derived here. */
  won: boolean;
  /** Whether this viewer answered with it. */
  yours: boolean;
}

/** A settled vote, as one card's worth of numbers. */
export interface VoteReadout {
  /**
   * The winning answer's *label*, or null when nothing won.
   *
   * Null is a real outcome and the commonest way for a vote to move nothing: nobody answered, or
   * two answers tied. **A tie is deliberately not broken** — every way of breaking one names a
   * winner from something that is not the vote — so the card says so rather than picking one.
   */
  winner: string | null;
  rows: readonly VoteResultRow[];
  /** How many answers were counted, across every option. */
  totalVotes: number;
  /** What actually changed hands, which is what the losers paid rather than what they were asked. */
  massMoved: number;
  /**
   * Signed mass this viewer gained or paid, or null when they were not part of the settlement.
   *
   * Null covers three true statements at once — an abstainer, a spectator, and a voter whose run
   * ended inside the ten seconds — which is exactly why the server sends null for all three
   * rather than a zero somebody would have to tell apart from a floored loser (`VoteSelfSnapshot`).
   */
  massChange: number | null;
}

/**
 * A settled vote, joined against the ballot it was cast on.
 *
 * The rows are in the **ballot's** order rather than the tallies', and each tally is looked up by
 * id rather than by position: `voteTallySchema` carries an `optionId` precisely so that neither
 * end has to agree about ordering, and reading it by index would be the same mistake answering a
 * vote by index would be one direction along the wire.
 *
 * An option with no tally on the frame reads as zero votes rather than being dropped — a ballot
 * is what the room asked, so an answer nobody picked is part of the answer.
 */
export function readVoteResult(
  options: readonly VoteOption[],
  result: VoteResultSnapshot,
  self: VoteSelfSnapshot | null,
): VoteReadout {
  const counts = new Map(result.tallies.map((tally) => [tally.optionId, tally.votes]));
  const totalVotes = options.reduce((sum, option) => sum + (counts.get(option.id) ?? 0), 0);
  const rows = options.map((option) => {
    const votes = counts.get(option.id) ?? 0;
    return {
      id: option.id,
      label: option.label,
      votes,
      share: totalVotes === 0 ? 0 : votes / totalVotes,
      won: result.winner === option.id,
      yours: self?.choice === option.id,
    };
  });
  return {
    winner: options.find((option) => option.id === result.winner)?.label ?? null,
    rows,
    totalVotes,
    massMoved: result.massMoved,
    massChange: self?.massChange ?? null,
  };
}

/**
 * Whole seconds left to answer, never below zero.
 *
 * A `ceil`, so the number changes when the remainder runs out and at no other moment — which is
 * what lets the card wake on its own second boundaries instead of on a fixed interval, exactly as
 * the duel's does. A deadline already past reads 0 rather than a negative: a card whose frame
 * arrived late is a card with no time left, not a card that owes time.
 */
export function voteSecondsLeft(endsAtMs: number, nowMs: number): number {
  return Math.max(0, Math.ceil((endsAtMs - nowMs) / 1000));
}

/**
 * A signed mass change, for the one line on the results card that is about the reader.
 *
 * Rounded for reading rather than for arithmetic, which is the treatment every other mass in the
 * HUD gets (`DuelOverlay`'s stake). The sign is explicit on a gain because "+312" and "312" are
 * the difference between a payout and a total, and a loss carries its own.
 */
export function formatVoteMass(change: number): string {
  const rounded = Math.round(change);
  return `${rounded > 0 ? '+' : ''}${rounded.toLocaleString()}`;
}
